//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';

import { TScheduledPostAttemptData } from '@apis/universal-posts/scheduled-posts/postAttempts/universalScheduledPostAttemptsModel';
import Cuid from '@models/Cuid';
import DraftId from '@models/DraftId';
import PinId from '@models/PinId';
import PinterestBoardId from '@models/PinterestBoardId';
import PinterestBoardSectionId from '@models/PinterestBoardSectionId';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import { WeekDayValue } from '@utils/moment';
import { Normalized } from '@utils/normalize';

import { RoutableStateName } from 'app/top-level-states';

export interface IPinterestPostDetailsState
  extends IStaticPinterestPostDetails {
  currentPostDetails: IComparablePinterestPostDetails;
  initialPostDetails: IComparablePinterestPostDetails;

  currentPinId: PinId;
  scheduleType: PinterestScheduleType;
  schedulingStep: PinterestPostDetailsSchedulingStep;
  previewCuidIndex: number;
  schedulingEntryPoint?: RoutableStateName;

  errors: IPinterestPostDetailsErrors;
  flags: IPinterestPostDetailsFlags;
}

export enum PinterestScheduleType {
  Single = 'Single',
  Bulk = 'Bulk',
}

export enum PinterestPostDetailsSchedulingStep {
  MediaDetails = 1,
  ScheduleDetails = 2,
}

/**
 * Information about the current post that cannot be altered
 * by any user input in the post-details form.
 */
export interface IStaticPinterestPostDetails {
  draftId?: DraftId;
  postId?: PostableId;
  postStatus?: PostableStatus;
  failedAttempts?: TScheduledPostAttemptData[];
}

/**
 * Information about the current post that can be directly
 * altered by user input in the post details form.
 */
export interface IComparablePinterestPostDetails {
  pins: Normalized<IPinterestPostDetailsPin, PinId>;
  bulkScheduleDetails: IBulkPinScheduleDetails;
}

export interface IPinterestPostDetailsPin {
  id: PinId;
  boardId?: PinterestBoardId;
  boardSection?: PinterestBoardSectionId;
  cuids: Cuid[];
  description?: string;
  destinationLink?: string;
  scheduledTime?: Date;
  title?: string;

  validationErrors: PojoSet<PinterestPostDetailsValidationError>;
  invalidCuids: PojoSet<Cuid>;
  originalScheduledTime?: Date;
}

export interface IBulkPinScheduleDetails {
  isBulkBoardSelectEnabled: boolean;
  bulkBoardSelectBoardId?: PinterestBoardId;
  bulkBoardSelectBoardSectionId?: PinterestBoardSectionId;
  daysOfWeek: WeekDayValue[];
  pinsPerDay?: number;
  postingInterval?: PinPostingIntervalValue;
  startScheduledTime?: Date;

  validationErrors: PojoSet<PinterestPostDetailsBulkScheduleValidationError>;
}

// Posting interval options as number of minutes
export const PIN_POSTING_INTERVAL_VALUES = [0, 15, 30, 45, 60, 120] as const;
export type PinPostingIntervalValue =
  typeof PIN_POSTING_INTERVAL_VALUES[number];

interface IPinterestPostDetailsErrors {
  draftFailureError?: string;
  setInitialPostDetailsError?: string;
  fetchSelectedMediaError?: string;
  scheduleFailureError?: string;
}

interface IPinterestPostDetailsFlags {
  hasSetPostDetails: boolean;
  isDraftingPin: boolean;
  isFetchingSelectedMedia: boolean;
  isInitializingBulkPinMedia: boolean;
  isSchedulingPin: boolean;
}

export enum PinterestPostDetailsValidationError {
  DescriptionTooLong = 'DescriptionTooLong',
  InvalidDestinationLink = 'InvalidDestinationLink',
  MediaTooSmall = 'MediaTooSmall',
  NoMedia = 'NoMedia',
  NoBoard = 'NoBoard',
  NoPostTime = 'NoPostTime',
  PostTimeInPast = 'PostTimeInPast',
  TitleTooLong = 'TitleTooLong',
  VideoTooLong = 'VideoTooLong',
}

export enum PinterestPostDetailsBulkScheduleValidationError {
  NoBulkBoardSelected = 'NoBulkBoardSelected',
  NoDayOfWeek = 'NoDayOfWeek',
  NoPinsPerDay = 'NoPinsPerDay',
  NoPostingInterval = 'NoPostingInterval',
  NoStartPostTime = 'NoStartPostTime',
  StartPostTimeInPast = 'StartPostTimeInPast',
}

export const MAX_PIN_TITLE_LENGTH = 100;
export const MAX_PIN_DESCRIPTION_LENGTH = 500;

// Max number of pins that can be scheduled using the bulk schedule endpoint
export const MAX_BULK_PIN_LIMIT = 50;

// Max number of images in a carousel pin
export const MAX_CAROUSEL_PIN_IMAGES = 5;

export const MAX_VIDEO_DURATION_MILLIS = 900000; // 15 minutes in millis
export const MIN_MEDIA_WIDTH_PX = 200;
export const MIN_MEDIA_HEIGHT_PX = 300;

export enum PinterestPostAttemptError {
  Authentication = 'authentication',
  BoardAuthorization = 'board_authorization',
  Credentials = 'credentials',
  MaxAttempts = 'max_attempts',
  SourceDeleted = 'source_deleted',
  NoBoard = 'noboard',
  SmallImage = 'small_image',
  LargeImage = 'large_image',
  BrokenImage = 'broken_image',
  PostedBrokenImage = 'posted_broken_image',
  WritesBlocked = 'writes_blocked',
  InvalidSource = 'invalid_source',
  InvalidParameters = 'invalid_parameters',
  Content = 'content',
  PushNotification = 'push_notification',
  PushToken = 'push_token',
  Device = 'device',
  RequestTimeout = 'request_timeout',
  NotFound = 'not_found',
}

export const PinterestErrorCodes = {
  BoardNotFound: 40,
};
